import {gql, useLazyQuery, useQuery} from '@apollo/client';
import {useParams} from 'react-router-dom';
import {useState, useEffect, useMemo, useCallback} from 'react';

import {GetProducts, GetProductsVariables} from '../schema-types/GetProducts';
import {
  GetProductProviders,
  GetProductProvidersVariables,
} from '../schema-types/GetProductProviders';
import {
  OptionValueType,
  SelectGroupOption,
  SelectOption,
} from '../components/AutoForm/AutoFieldNew';
import {useAccountContext} from '../contexts/AccountContext';
import {GET_PRODUCTS, GET_PRODUCT_PROVIDERS} from '../features/goals/goalsQueries';
import {includesSaltus} from '../utils/includesSaltus';
import {BusinessUnitEnum} from '../schema-types/globalTypes';
import {PotDetails_productProvider} from '../schema-types/PotDetails';

const stocksAndShares = 'ISA - Stocks and Shares';
const alphaSortWithExceptions = (a: SelectOption, b: SelectOption): number => {
  if (a.label === stocksAndShares && b.label.includes('ISA')) {
    return -1;
  }
  if (b.label === stocksAndShares && a.label.includes('ISA')) {
    return 1;
  }
  if (a.label > b.label) {
    return 1;
  }
  if (a.label < b.label) {
    return -1;
  }
  return 0;
};

type PlanProviderOptionAdditional = Pick<
  PotDetails_productProvider,
  'showMandateSelector'
> & {
  isSaltus: boolean;
};

export type PlanProviderSelectOption = SelectOption<string> &
  PlanProviderOptionAdditional;

export default function usePlanNamesProviders() {
  const {accountType} = useAccountContext();

  const {accountId} = useParams();
  const [queryGetProducts, {data: names, loading: namesLoading}] = useLazyQuery<
    GetProducts,
    GetProductsVariables
  >(GET_PRODUCTS);
  const [queryGetProductProviders, {data: rawProviders, loading: providersLoading}] =
    useLazyQuery<GetProductProviders, GetProductProvidersVariables>(
      GET_PRODUCT_PROVIDERS
    );

  const [productProviders, setProductProviders] = useState<PlanProviderSelectOption[]>();
  const [productNames, setProductNames] = useState<SelectOption<string>[]>();
  const [subproductProviders, setSubproductProviders] =
    useState<PlanProviderSelectOption[]>();
  const [productProvidersOptionGroups, setProductProvidersOptionGroups] = useState<
    SelectGroupOption<OptionValueType, PlanProviderSelectOption>[]
  >([]);
  const [shellProducts, setShellProducts] = useState<SelectOption<string>[]>();

  useEffect(() => {
    if (!names?.products) {
      return;
    }
    const newProductNames = names.products
      .map<SelectOption<string>>((x) => {
        const label = x.name! || x!.formattedName!;

        return {
          value: x!.id,
          label: label.toLocaleLowerCase() === 'isa' ? stocksAndShares : label,
        };
      })
      .sort(alphaSortWithExceptions);

    setProductNames(newProductNames);

    // business rules for shell plans
    // * filtered: Product Type = Investment or Retirement
    // * filtered: Product Attribute <> Deposit Based or Annuity Based
    // * Sort: alphabetical
    const newShellNames = names.products
      .filter(
        (x) =>
          x.productBasis?.toLowerCase().includes('investment') ||
          x.productBasis?.toLowerCase().includes('retirement')
      )
      .map<SelectOption<string>>((x) => {
        const label = x.name! || x!.formattedName!;
        return {
          value: x!.id,
          label: label.toLocaleLowerCase() === 'isa' ? stocksAndShares : label,
        };
      })
      .sort(alphaSortWithExceptions);

    setShellProducts(newShellNames);
  }, [names?.products]);

  const lisaId = useMemo(() => {
    return names?.products?.find((x) => x.formattedName === 'L-ISA')?.id;
  }, [names?.products]);

  useEffect(() => {
    if (!rawProviders?.productProviders) {
      return;
    }

    const items: Record<'favourite' | 'panel' | 'other', PlanProviderSelectOption[]> = {
      favourite: [],
      panel: [],
      other: [],
    };

    rawProviders.productProviders.forEach((x) => {
      const item: PlanProviderSelectOption = {
        value: x!.id,
        label: x.name! || x!.formattedName!,
        // eslint-disable-next-line no-nested-ternary
        group: x.isFavourite
          ? 'Favourite'
          : x.isPanel && accountType !== BusinessUnitEnum.DA
          ? 'Panel'
          : 'Other',
        isSaltus: includesSaltus(x.formattedName) || includesSaltus(x.name),
        showMandateSelector: x.showMandateSelector,
      };
      if (x.isFavourite) {
        items.favourite.push(item);
      } else if (x.isPanel && accountType !== BusinessUnitEnum.DA) {
        items.panel.push(item);
      } else {
        items.other.push(item);
      }
    });

    setProductProvidersOptionGroups([
      {groupLabel: 'Favourite', options: items.favourite},
      {groupLabel: 'Panel', options: items.panel},
      {groupLabel: 'Other', options: items.other},
    ]);

    const sortedFlat = [];
    if (items.favourite.length) {
      sortedFlat.push(...items.favourite.sort(alphaSortWithExceptions));
    }
    if (items.panel.length) {
      sortedFlat.push(...items.panel.sort(alphaSortWithExceptions));
    }
    if (items.other.length) {
      sortedFlat.push(...items.other.sort(alphaSortWithExceptions));
    }

    setProductProviders(
      sortedFlat
      // rawProviders.productProviders
      //   .map<SelectOption>((x) => ({
      //     value: x!.id,
      //     label: x!.formattedName! || x.name!,
      //   }))
      //   .sort(alphaSort)
    );
  }, [rawProviders?.productProviders]);

  useEffect(() => {
    if (!rawProviders?.subplanProductProvders) {
      return;
    }

    const newSubplanProviders: PlanProviderSelectOption[] =
      rawProviders.subplanProductProvders
        .map<PlanProviderSelectOption>((x) => ({
          value: x!.id,
          label: x!.formattedName! || x.name!,
          showMandateSelector: x.showMandateSelector,
          isSaltus: includesSaltus(x.formattedName) || includesSaltus(x.name),
        }))
        .sort(alphaSortWithExceptions);
    setSubproductProviders(newSubplanProviders);
  }, [rawProviders?.subplanProductProvders]);

  const isSaltusProvider = useCallback(
    (id?: string) => {
      if (!id) {
        return false;
      }
      return includesSaltus(
        rawProviders?.productProviders?.find((x) => x.id === id)?.name
      );
    },
    [rawProviders?.productProviders]
  );

  return {
    productNames,
    productProviders,
    queryGetProductProviders,
    subproductProviders,
    queryGetProducts,
    rawProviders,
    productProvidersOptionGroups,
    isSaltusProvider,
    namesLoading,
    shellProducts,
    names,
    lisaId,
  };
}
